import type { GridApi, IRowNode } from 'ag-grid-community';
import type { ICellRendererParams } from 'ag-grid-enterprise';
import type { CustomCellEditorProps } from 'ag-grid-react';
import type { ReactNode } from 'react';
import type { Currency } from '../../types/Currency';
import type { Security } from '../../types/Security';
import type { RequiredProperties } from '../../utils';
import type { SizeValue } from '../BlotterTable/aggFuncs/types';
import type { BlotterTableAgGridContext } from '../BlotterTable/BlotterTableContext';
import type { SizeColumnParams } from '../BlotterTable/columns/size.types';
import type { WarningColumnValue } from '../BlotterTable/columns/warning.types';
import type { ButtonProps, IconButtonProps } from '../Button';
import type { MultiSelectProps } from '../Form';
import type { UseDropdownPopperProps } from '../Form/Dropdown';
import type { SearchSelectProps, UseSearchSelectProps } from '../Form/SearchSelect/types';
import type { InlineFormattedNumberProps, NumberVariants } from '../FormattedNumber';

export const WARNING_ROW_CLASSNAME = 'ag-row-warning-severity-high';
export const FADED_ROW_CLASSNAME = 'ag-row-faded';
export const POSITIVE_ROW_CLASSNAME = 'ag-row-positive-highlight';
export const NEGATIVE_ROW_CLASSNAME = 'ag-row-negative-highlight';

export const WARNING_CELL_MED_CLASSNAME = 'ag-cell-warning-severity-medium';
export const POSITIVE_CELL_CLASSNAME = 'ag-cell-positive-highlight';
export const POSITIVE_CELL_LIGHTEN_CLASSNAME = 'ag-cell-positive-lighten';
export const POSITIVE_CELL_DIM_CLASSNAME = 'ag-cell-positive-dim';
export const POSITIVE_CELL_DARK_CLASSNAME = 'ag-cell-positive-dark';
export const NEGATIVE_CELL_CLASSNAME = 'ag-cell-negative-highlight';
export const NEGATIVE_CELL_LIGHTEN_CLASSNAME = 'ag-cell-negative-lighten';
export const NEGATIVE_CELL_DIM_CLASSNAME = 'ag-cell-negative-dim';
export const NEGATIVE_CELL_DARK_CLASSNAME = 'ag-cell-negative-dark';
export const LIGHTEN_CELL_CLASSNAME = 'ag-cell-lighten';
export const SUBTLE_TEXT_CELL_CLASSNAME = 'ag-cell-text-subtle';
export const FADED_CELL_CLASSNAME = 'ag-cell-faded';

export interface AgGridWarningProps extends ICellRendererParams<unknown, WarningColumnValue | undefined> {
  tooltip?: ReactNode;
  tooltipWidth?: string;
}

export type AgGridSearchSelectDropdownProps<T> = CustomCellEditorProps & {
  useSearchSelectParams: Omit<UseSearchSelectProps<T>, 'onChange' | 'selection' | 'inputRef'>;
  useDropdownParams?: Omit<UseDropdownPopperProps, 'isOpen' | 'referenceElement'>;
} & Pick<
    SearchSelectProps<T>,
    'maxHeight' | 'showClear' | 'showDropdownSearch' | 'searchPlaceholder' | 'portalize' | 'size'
  >;

export type AgGridMultiSelectDropdownProps<T> = CustomCellEditorProps & {
  multiSelectProps: Omit<MultiSelectProps<T>, 'onChange' | 'selection' | 'inputRef' | 'selections'>;
} & Pick<SearchSelectProps<T>, 'maxHeight' | 'showDropdownSearch' | 'searchPlaceholder' | 'portalize'>;

export type AgGridSizeProps = Pick<
  SizeColumnParams,
  | 'sentiment'
  | 'getSentiment'
  | 'highlightNegative'
  | 'round'
  | 'truncate'
  | 'trimTrailingZeroes'
  | 'omitZero'
  | 'siblingLeafPrecision'
> &
  ICellRendererParams<unknown, SizeValue> & {
    inlineFormattedNumberAlignment?: InlineFormattedNumberProps['align'];
    tooltip?: ReactNode;
  };

export interface AgGridButtonProps extends Omit<ButtonProps, 'onClick' | 'disabled'> {
  onClick(params: { node: IRowNode; api: GridApi }): void;
  disabled?: boolean | ((params: { node: IRowNode; api: GridApi }) => boolean);
  hide?: (data: IRowNode['data']) => boolean;
}

export type AgGridCurrencyParams = Omit<AgGridCurrencyProps, 'value'>;

export type AgGridCurrencyProps = {
  currencies?: Map<string, RequiredProperties<Partial<Currency>, 'Symbol' | 'Description'>>;
  /** show/hide description, true by default */
  showDescription?: boolean;
  /** show/hide icon, true by default */
  showIcon?: boolean;
  /** show/hide colorful icons, false by default */
  colorful?: boolean;
  iconSize?: number;
  /**
   * (Security mode) If the value is to be derived from a Security value,
   * define the key for the Security
   */
  symbolField?: string;
  /**
   * (Security mode) If the value is to be derived from a Security value,
   * define the property for the value on the Security
   */
  securityCurrencyField?: string;
};

export type AgGridFormattedNumberProps = {
  highlightNegative?: boolean;
  unit?: string;
  currencies?: Map<string, any>;
  currencyField?: string;
  sentiment?: NumberVariants;
  showCurrency?: boolean;
  round?: boolean;
  increment?: string;
};

export type AgGridIconButtonProps<TData = any> = Omit<IconButtonProps, 'onClick'> & {
  onClick?: (params: { node: IRowNode<TData>; api: GridApi<TData> }) => void;
  hide?: (params: { node: IRowNode<TData>; api: GridApi<TData> }) => boolean;
  /**
   * The data-value of the row for the icon button, useful for testing.
   */
  dataValue?: string;
};

export const SecurityToursEnum = {
  DDH_ENABLED_ORDER: 'DDH_ENABLED_ORDER',
} as const;
export type SecurityToursEnum = (typeof SecurityToursEnum)[keyof typeof SecurityToursEnum];

export type AgGridSecurityDecorationParams = {
  data: unknown;
  security: Security | undefined;
  context: BlotterTableAgGridContext;
};

export type AgGridSecurityProps = {
  showIcon?: boolean;
  showDescription?: boolean;
  unifiedLiquidityIndicator?: ReactNode | ((params: AgGridSecurityDecorationParams) => ReactNode);
  colorful?: boolean;
  showAsteriskOnEmpty?: boolean;
  suffix?: ReactNode | ((params: AgGridSecurityDecorationParams) => ReactNode);
  tours?: SecurityToursEnum[];
};

export type AgGridUserProps = {
  /** Define by which property and in which map we should look for the user entry */
  valueFormatWithMap?: 'email' | 'id';
  /** Define what value to display (displayName or email) */
  displayValueAs?: 'displayName' | 'email';
};
